import { CodeChip } from '@/components/reusable/code-chip/CodeChip';
import { expectedFor, type IUnitEstimate, useCancellationImpact } from '@/hooks/queries/cancellations/use-cancellation-impact';

import { placeLine, plural, reasonLabel, sourceLine, STATE_COPY, timeOf, unitTitle } from '../copy';
import { type ICancellationScope } from '../hooks/use-cancellation-scope';
import { type ICancellationAlert } from '../services/cancellation-alerts';
import { type ICancellationCapabilities, type ICancellationRow } from '../types';
import CancelledChip from './CancelledChip';
import ImplicationsLine from './ImplicationsLine';
import RowActions from './RowActions';

interface ICancellationCard {
	row: ICancellationRow;
	/** False once withdrawn / reinstated / gone — drawn struck through, never removed silently. */
	active: boolean;
	unread?: boolean;
	/** Only the newest unread alert announces itself (`role="alert"`). */
	announce?: boolean;
	scope: ICancellationScope;
	capabilities: ICancellationCapabilities;
	estimates: Map<string, IUnitEstimate> | null;
	onOpen: () => void;
	onDismiss?: () => void;
	onDone?: () => void;
}

/** The chip names the row's real state — a reinstated unit is not "withdrawn". */
export const chipLabel = (row: ICancellationRow, active: boolean): string => {
	if (active) return 'Cancelled';
	if (row.state === 'Reinstated') return 'Reinstated';
	if (row.state === 'Withdrawn') return 'Withdrawn';
	return 'No longer cancelled';
};

const inactiveLabel = (row: ICancellationRow): string => {
	if (row.state === 'Withdrawn') return STATE_COPY.Withdrawn;
	if (row.state === 'Reinstated') return STATE_COPY.Reinstated;
	return 'No longer on the cancelled list';
};

/**
 * One cancelled unit: who, where, why, when, from whom — and, on its own line, what it costs.
 * Shared by the shell alert stack and the page's "Needs review" list.
 */
export const CancellationCard = ({
	row,
	active,
	unread = false,
	announce = false,
	scope,
	capabilities,
	estimates,
	onOpen,
	onDismiss,
	onDone,
}: ICancellationCard) => {
	const impact = useCancellationImpact({
		electionId: scope.electionId,
		partyId: scope.partyId,
		partyKey: scope.partyKey,
		id: row.id,
		enabled: capabilities.impact && active,
	});
	const title = unitTitle(row);
	const evidence = typeof row.evidenceCount === 'number' ? row.evidenceCount : null;

	return (
		<article
			className={`pc-card${active ? '' : ' pc-card--inactive'}${unread ? ' pc-card--unread' : ''}`}
			role={announce ? 'alert' : undefined}
			aria-label={`${active ? 'Cancelled' : 'No longer cancelled'}: ${title}`}
		>
			<div className='pc-card__head'>
				<div className='pc-card__title-row'>
					<CancelledChip label={chipLabel(row, active)} />
					<h3 className='pc-card__title'>{title}</h3>
					{onDismiss ? (
						<button
							type='button'
							className='pc-card__dismiss'
							onClick={onDismiss}
							aria-label={`Dismiss alert for ${title}`}
						>
							×
						</button>
					) : null}
				</div>
				<div className='pc-card__meta'>
					<CodeChip
						code={row.pollingUnitCode}
						level='pollingUnit'
						compact
					/>
					<span>{placeLine(row)}</span>
				</div>
			</div>

			<p className='pc-card__facts'>
				<strong>{reasonLabel(row.reason)}</strong>
				<span aria-hidden='true'> · </span>
				<span>{timeOf(row.cancelledAt ?? row.recordedAt)}</span>
				<span aria-hidden='true'> · </span>
				<span>{sourceLine(row)}</span>
				{evidence !== null ? (
					<>
						<span aria-hidden='true'> · </span>
						<span>{evidence === 0 ? 'No evidence yet' : plural(evidence, 'evidence file')}</span>
					</>
				) : null}
				{row.reviewStatus === 'Unreviewed' && active ? (
					<>
						<span aria-hidden='true'> · </span>
						<span className='pc-tag pc-tag--review pc-tag--review'>Not yet reviewed</span>
					</>
				) : null}
			</p>

			{active ? (
				<ImplicationsLine
					impact={capabilities.impact ? impact.data : undefined}
					expectedVotes={expectedFor(estimates, row.pollingUnitCode)}
				/>
			) : (
				<p className='pc-card__inactive-note'>
					{inactiveLabel(row)}
					{row.stateReason && row.stateReason !== 'null' ? ` — ${row.stateReason}` : ''}
				</p>
			)}

			<RowActions
				row={row}
				scope={scope}
				capabilities={capabilities}
				onOpen={onOpen}
				onDone={onDone}
				size='xs'
			/>
		</article>
	);
};

interface IAlertCard extends Omit<ICancellationCard, 'row' | 'active' | 'unread'> {
	alert: ICancellationAlert;
}

const AlertCard = ({ alert, ...rest }: IAlertCard) => (
	<CancellationCard
		row={alert.row}
		active={alert.active}
		unread={alert.unread}
		{...rest}
	/>
);

export default AlertCard;
